import { BiSubdirectoryRight } from 'react-icons/bi';
import { RoleTag } from './RoleTag';
import { TitleFragment } from './TitleFragment';
import './CompactRole.scss';

interface UrlInfo {
  id: string;
  urlId: string | null;
  url: string;
  urlType: string | null;
  screenshotKey: string | null;
  textKey: string | null;
  createdAt: string;
}

interface Evidence {
  id?: string;
  observedAt?: string;
  normalizedTitle?: string;
  sourceType?: string;
  url?: UrlInfo | null;
}

interface Role {
  role?: string;
  area?: string;
  modifier?: string;
  decisionLevel?: string | null;
  functionalGroup?: string | null;
  evidence?: Evidence[];
}

interface ScreenshotData {
  screenshot: string;
  screenshot_size: null;
  boxes: [];
}

interface CompactRoleProps {
  role: Role;
  onSelectScreenshot?: (data: ScreenshotData) => void;
}

export function CompactRole({ role, onSelectScreenshot }: CompactRoleProps) {
  const hasContent = role.modifier || role.role || role.area || role.decisionLevel || role.functionalGroup;

  if (!hasContent) {
    return null;
  }

  // Group evidence by normalized title, collecting all source types
  // For CRAWLER sources, pick the best evidence (most recent, with screenshot as tiebreaker)
  interface TitleGroup {
    title: string;
    hasDistrictVerification: boolean; // STATE_DATA or FOIA
    hasWebObservation: boolean; // CRAWLER
    screenshotKey: string | null; // best screenshot from CRAWLER evidence
    domain: string | null; // domain from URL for web observations
    fullUrl: string | null; // full URL for linking
  }

  // Extract domain from URL
  const extractDomain = (url: string | undefined | null): string | null => {
    if (!url) return null;
    try {
      const parsed = new URL(url);
      return parsed.hostname.replace(/^www\./, '');
    } catch {
      return null;
    }
  };

  const titleMap = new Map<string, TitleGroup>();

  (role.evidence || []).forEach(e => {
    if (!e.normalizedTitle || !e.sourceType) return;

    const title = e.normalizedTitle;
    const existing = titleMap.get(title);

    if (!existing) {
      titleMap.set(title, {
        title,
        hasDistrictVerification: e.sourceType === 'STATE_DATA' || e.sourceType === 'FOIA',
        hasWebObservation: e.sourceType === 'CRAWLER',
        screenshotKey: e.sourceType === 'CRAWLER' ? (e.url?.screenshotKey || null) : null,
        domain: e.sourceType === 'CRAWLER' ? extractDomain(e.url?.url) : null,
        fullUrl: e.sourceType === 'CRAWLER' ? (e.url?.url || null) : null,
      });
    } else {
      // Merge sources
      if (e.sourceType === 'STATE_DATA' || e.sourceType === 'FOIA') {
        existing.hasDistrictVerification = true;
      }
      if (e.sourceType === 'CRAWLER') {
        existing.hasWebObservation = true;
        // Update screenshot/domain/url if this evidence is better (has screenshot and existing doesn't)
        if (e.url?.screenshotKey && !existing.screenshotKey) {
          existing.screenshotKey = e.url.screenshotKey;
          existing.domain = extractDomain(e.url?.url);
          existing.fullUrl = e.url?.url || null;
        }
        // If no screenshot yet but we have domain info, use it
        if (!existing.domain && e.url?.url) {
          existing.domain = extractDomain(e.url.url);
          existing.fullUrl = e.url.url;
        }
      }
    }
  });

  const titleGroups = Array.from(titleMap.values());

  const handleScreenshotClick = (screenshotKey: string) => {
    if (onSelectScreenshot) {
      // Normalize the key - strip "url/" prefix if present since the modal adds it
      // ES data may have "url/..." while other sources pass just the path
      const key = screenshotKey.startsWith('url/') ? screenshotKey.slice(4) : screenshotKey;
      onSelectScreenshot({
        screenshot: key,
        screenshot_size: null,
        boxes: []
      });
    }
  };

  return (
    <div className="compact-role">
      <BiSubdirectoryRight className="compact-role__icon" size={18} />
      <div className="compact-role__main">
        <div className="compact-role__content">
          {role.decisionLevel && <RoleTag type="decisionLevel" value={role.decisionLevel} size="small" bg="medium" />}
          {role.functionalGroup && <RoleTag type="functionalGroup" value={role.functionalGroup} size="small" bg="medium" />}
          {role.modifier && <TitleFragment label="Modifier" value={role.modifier} size="small" colorMode="grayscale" />}
          {role.role && <TitleFragment label="Role" value={role.role} size="small" colorMode="grayscale" />}
          {role.area && <TitleFragment label="Area" value={role.area} size="small" colorMode="grayscale" />}
        </div>
        {titleGroups.length > 0 && (
          <div className="compact-role__observations">
            {titleGroups.map((item, idx) => (
              <div key={idx} className="compact-role__observation">
                <BiSubdirectoryRight className="compact-role__obs-icon" size={14} />
                <span className="compact-role__obs-text">
                  <span className="compact-role__obs-title">{item.title}</span>
                  {' '}
                  {item.hasDistrictVerification && item.hasWebObservation ? (
                    <>
                      verified by the district and observed on{' '}
                      {item.screenshotKey ? (
                        <span
                          className="compact-role__web-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleScreenshotClick(item.screenshotKey!);
                          }}
                        >
                          {item.domain || 'web'}
                        </span>
                      ) : (
                        item.domain || 'web'
                      )}
                    </>
                  ) : item.hasDistrictVerification ? (
                    'verified by the district'
                  ) : item.hasWebObservation ? (
                    <>
                      observed on{' '}
                      {item.screenshotKey ? (
                        <span
                          className="compact-role__web-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleScreenshotClick(item.screenshotKey!);
                          }}
                        >
                          {item.domain || 'web'}
                        </span>
                      ) : (
                        item.domain || 'web'
                      )}
                    </>
                  ) : null}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
